import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, Mail, Crown, Users, Eye, Settings, ShieldCheck,
  ChevronRight, X, Stethoscope, Building2,
} from "lucide-react";
import SEO from "../components/SEO";
import { getIdTypeMeta, getUserBadge } from "../lib/userIdTypes";
import { API } from "../apiBase";
const TABS = [
  { id: "consultant",   label: "Consultant",   Icon: Eye,
    desc: "Accès en lecture · Consultation des messages · Pas de modification des paramètres." },
  { id: "modificateur", label: "Modificateur", Icon: Settings,
    desc: "Gestion des accès · Modification de la boîte · Ajout / suppression d'utilisateurs · Paramétrage avancé." },
];

export default function MssanteUserAcces() {
  const { uid } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [tab, setTab] = useState("consultant");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/pro/users/${uid}/access`);
        if (alive) setData(data);
      } catch (e) {
        if (alive) setErr(e?.response?.status === 404 ? "Utilisateur introuvable." : "Impossible de charger les droits.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [uid]);

  const u = data?.user;
  const badge = getUserBadge(u?.id_type);
  const idMeta = getIdTypeMeta(u?.id_type);

  // Modificateur tab hides delegataires (read-only role -> no admin rights)
  const showDelegataires = tab === "consultant";

  return (
    <div className="bg-[var(--mc-navy-950)] min-h-screen" data-testid="page-mssante-user-acces">
      <SEO title="Droits d'accès · MetaConnect" canonical={`/mssante/espace/utilisateurs/${uid}/acces`} noIndex />

      <section className="hex-grid pt-32 pb-10">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <button onClick={() => navigate(-1)}
                  className="text-[12px] text-[#475569] hover:text-[#0F2B5B] inline-flex items-center gap-1.5">
            <ArrowLeft size={12} /> Retour
          </button>
          <div className="mt-4 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-emerald-600">
            <ShieldCheck size={13} /> Droits d'accès utilisateur
          </div>
          <h1 className="mt-3 font-display text-[#0F2B5B] text-3xl sm:text-4xl font-semibold leading-tight">
            {u ? `Droits d'accès — ${u.first_name} ${u.last_name}` : "Droits d'accès"}
          </h1>
          {u && (
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              {badge && (
                <span className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] px-2.5 py-1 rounded-full border ${
                  badge.kind === "soignant"
                    ? "border-[rgba(110,231,197,0.4)] text-emerald-600 bg-[rgba(110,231,197,0.06)]"
                    : "border-[rgba(127,179,255,0.32)] text-[#0F2B5B] bg-[#EFF6FF]"
                }`}>
                  {badge.kind === "soignant" ? <Stethoscope size={11} /> : <Building2 size={11} />}
                  {badge.label}
                </span>
              )}
              <span className="text-[12.5px] text-[#475569]">
                {u.profession}
                {idMeta && u.professional_id && <> · {idMeta.label} <span className="font-mono text-[#475569]">{u.professional_id}</span></>}
                <span className="mx-2">·</span>{u.email}
              </span>
            </div>
          )}
        </div>
      </section>

      <section className="hex-soft py-8 lg:py-12">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          {loading && <div className="text-[#475569] text-[13px]">Chargement…</div>}
          {err && <div className="text-[#FFB4B4] text-[13px]">{err}</div>}

          {!loading && data && (
            <div className="grid gap-5">
              {/* Tabs */}
              <div data-testid="user-acces-tabs">
                <div className="flex items-center gap-2 flex-wrap">
                  {TABS.map((t) => {
                    const active = tab === t.id;
                    const Icon = t.Icon;
                    return (
                      <button key={t.id} onClick={() => setTab(t.id)}
                              data-testid={`user-acces-tab-${t.id}`}
                              className={`px-4 py-2 rounded-full text-[12px] font-medium inline-flex items-center gap-2 border transition ${
                                active
                                  ? "bg-[var(--mc-blue-500)] border-[var(--mc-blue-500)] text-[#0F2B5B]"
                                  : "bg-transparent border-slate-200 text-[#475569] hover:text-[#0F2B5B] hover:border-[rgba(127,179,255,0.32)]"
                              }`}>
                        <Icon size={13} /> {t.label}
                      </button>
                    );
                  })}
                </div>
                <p className="mt-3 text-[12.5px] text-[#475569] max-w-2xl">
                  {TABS.find((t) => t.id === tab)?.desc}
                </p>
              </div>

              {/* Banner: source of truth */}
              <div className="rounded-lg border border-slate-200 bg-[#F8FAFC] p-4 text-[12.5px] text-[#475569] flex items-start gap-2">
                <ShieldCheck size={14} className="text-[#0369A1] mt-0.5 shrink-0" />
                <p>
                  Cette vue est <strong className="text-[#0F2B5B]">en lecture seule</strong>.
                  Pour modifier les droits, ouvrez la fiche de la boîte concernée et utilisez « Gestion des accès ».
                </p>
              </div>

              {/* Roles */}
              <RoleBlock title="Responsable" Icon={Crown}
                         emptyText="Aucune boîte aux lettres"
                         items={data.as_responsible}
                         testid="user-acces-responsable"
                         tone="mint" />
              <RoleBlock title="Cotitulaire" Icon={Users}
                         emptyText="Aucune boîte aux lettres"
                         items={data.as_cotitulaire}
                         testid="user-acces-cotitulaire"
                         tone="blue" />
              {showDelegataires && (
                <RoleBlock title="Délégataire" Icon={Eye}
                           emptyText="Aucune boîte aux lettres"
                           items={data.as_delegataire}
                           testid="user-acces-delegataire"
                           tone="violet" />
              )}

              {/* Footer */}
              <div className="flex items-center justify-end gap-2 pt-2">
                <button onClick={() => navigate(-1)} data-testid="user-acces-close"
                        className="btn-ghost px-5 py-2 rounded-full text-[12px] font-medium">
                  Fermer
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

const TONE = {
  mint:   { ring: "border-emerald-200", chip: "border-[rgba(110,231,197,0.4)] text-emerald-600 bg-[rgba(110,231,197,0.06)]",      iconBg: "bg-emerald-50 text-emerald-600" },
  blue:   { ring: "border-[#BAE6FD]", chip: "border-[rgba(127,179,255,0.32)] text-[#0F2B5B] bg-[#EFF6FF]", iconBg: "bg-[rgba(45,123,255,0.16)] text-[#0F2B5B]" },
  violet: { ring: "border-[rgba(168,140,255,0.30)]", chip: "border-[rgba(168,140,255,0.4)] text-[#d2c2ff] bg-[rgba(168,140,255,0.06)]",             iconBg: "bg-[rgba(168,140,255,0.12)] text-[#d2c2ff]" },
};

function RoleBlock({ title, Icon, items, emptyText, testid, tone = "blue" }) {
  const t = TONE[tone] || TONE.blue;
  return (
    <section className="mc-card p-5 lg:p-6" data-testid={testid}>
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="inline-flex items-center gap-2">
          <span className={`grid place-items-center h-9 w-9 rounded-full ${t.iconBg}`}><Icon size={14} /></span>
          <div>
            <div className="text-[#0F2B5B] font-medium">{title}</div>
            <div className="text-[11px] text-[#475569]">
              {items.length > 0 ? `${items.length} boîte${items.length > 1 ? "s" : ""}` : "Aucune boîte aux lettres"}
            </div>
          </div>
        </div>
        <span className={`text-[10px] uppercase tracking-[0.18em] px-2.5 py-1 rounded-full border ${t.chip}`}>
          {items.length}
        </span>
      </div>

      {items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-200 p-4 text-center text-[12.5px] text-[#475569]">
          {emptyText}
        </div>
      ) : (
        <div className="grid gap-2">
          {items.map((m) => (
            <Link key={m.id} to={`/mssante/espace/messagerie/${m.id}/acces`}
                  data-testid={`${testid}-item-${m.id}`}
                  className={`rounded-lg border ${t.ring} bg-[rgba(0,0,0,0.10)] hover:bg-[#F8FAFC] transition p-3 lg:p-4 flex items-center gap-3 flex-wrap`}>
              <Mail size={14} className={t.iconBg.includes("mint") ? "text-emerald-600" : t.iconBg.includes("blue") ? "text-[#0F2B5B]" : "text-[#d2c2ff]"} />
              <div className="flex-1 min-w-0">
                <div className="text-[#0F2B5B] font-medium text-[13.5px] break-all">{m.full_email}</div>
                <div className="text-[11.5px] text-[#475569] mt-0.5">
                  {m.structure_name}
                  <span className="mx-2">·</span>
                  Statut : {m.status === "active" ? "Active" : m.status === "cancelled" ? "Résiliée" : "En traitement"}
                </div>
              </div>
              <span className="text-[11px] text-[#475569] inline-flex items-center gap-1">
                Voir le détail <ChevronRight size={11} />
              </span>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}
